/**
 * Remaining `/settings/workspace/*` pages — #44574.
 *
 * Ports of archive, data, directory, domains, sections, team (+ subpages).
 * Live Next pages under excellent/app/settings/workspace/** are UNTOUCHED (D6).
 */

import * as React from "react";
import { createRoute } from "@tanstack/react-router";
import {
  FormField,
  Input,
  RoleGrantsEditor,
  SECTION_REGISTRY_UI,
  SectionEnablementList,
  SettingsForm,
  SettingsPageShell,
  SettingsSection,
  buttonVariants,
  type SectionEnablementVm,
} from "@excellent/ui";
import { apiCall, useApi } from "@excellent/ui/api";
import { sectionEnablement } from "@excellent/ui/api/contracts/section-enablement";
import { userSettings } from "@excellent/ui/api/contracts/user-settings";
import { DirectoryEntities } from "../shared/directory/views";
import { DomainsClient } from "../shared/settings/domains-client";
import {
  DevicesPanel,
  MembersPanel,
  OverviewPanel,
  PrimaryPanel,
} from "../shared/settings/team-panels";
import { rootRoute } from "./__root";

// ── /settings/workspace/archive ─────────────────────────────────────────────

function WorkspaceArchive() {
  return (
    <SettingsPageShell
      title="Archive"
      description="Restore sections, projects, and other objects you've archived or soft-deleted."
    >
      <SettingsSection
        id="archive"
        title="Archive"
        description="Nothing deleted stays gone forever until you purge. Restore one row at a time."
      >
        <p className="type-body text-muted-foreground">
          Archived sections and projects restore with their issues. Universal
          soft-delete rows (goals, issues, people, deals, jobs, workflows)
          restore via the object archive contract. When the vault has no
          archived rows, this page stays empty by design.
        </p>
      </SettingsSection>
    </SettingsPageShell>
  );
}

// ── /settings/workspace/data ────────────────────────────────────────────────

function WorkspaceData() {
  const { data: workspace } = useApi(userSettings.getWorkspace, {});
  const [msg, setMsg] = React.useState<string | null>(null);

  async function onSave(fd: FormData): Promise<void> {
    setMsg(null);
    try {
      await apiCall(userSettings.saveRetentionPolicyForm, {
        body: {
          retention_event_days: String(fd.get("retention_event_days") ?? ""),
          retention_raw_keep: String(fd.get("retention_raw_keep") ?? ""),
        },
      });
      setMsg("Retention policy saved.");
    } catch (e) {
      setMsg(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <SettingsPageShell
      title="Data"
      description="The local SQLite file plus the retention policy — where the workspace lives on disk, and the fail-closed levers for pruning old event payloads."
    >
      <SettingsSection
        id="sqlite"
        title="Local SQLite"
        description="Single-file vault. Path is machine-local and never syncs."
      >
        <p className="type-body text-muted-foreground">
          Workspace:{" "}
          <span className="font-medium text-foreground">
            {workspace?.name ?? "…"}
          </span>
          . The database file lives under your Excellent data directory (
          <code>data.db</code>).
        </p>
      </SettingsSection>
      <SettingsForm action={onSave} saveLabel="Save retention">
        <SettingsSection
          id="retention"
          title="Retention"
          description="Prune non-critical event payloads and compact superseded integration records."
        >
          <div className="max-w-md space-y-4">
            <FormField
              label="Event payload days"
              htmlFor="retention_event_days"
              help="Keep event payloads for this many days (blank = default)."
            >
              <Input
                id="retention_event_days"
                name="retention_event_days"
                placeholder="90"
              />
            </FormField>
            <FormField
              label="Raw record keep"
              htmlFor="retention_raw_keep"
              help="How many raw integration record generations to keep."
            >
              <Input
                id="retention_raw_keep"
                name="retention_raw_keep"
                placeholder="3"
              />
            </FormField>
          </div>
          {msg ? (
            <p className="mt-2 type-body text-muted-foreground">{msg}</p>
          ) : null}
        </SettingsSection>
      </SettingsForm>
    </SettingsPageShell>
  );
}

// ── /settings/workspace/directory ───────────────────────────────────────────

function WorkspaceDirectory() {
  return (
    <SettingsPageShell
      title="Directory"
      description="People and organizations across every product in this workspace. Products create these rows; this is the one place to manage them."
      actions={
        <a
          href="/operations/directory"
          className={buttonVariants({ variant: "outline", size: "sm" })}
        >
          Open in Operations
        </a>
      }
    >
      <DirectoryEntities workspaceId={1} />
    </SettingsPageShell>
  );
}

// ── /settings/workspace/domains ─────────────────────────────────────────────

function WorkspaceDomains() {
  // DomainsClient self-fetches domain rows and owns SettingsPageShell
  // title="Domains". kinds/views empty: list still works; composer is limited.
  return (
    <>
      {/* title="Domains" — SettingsPageShell in DomainsClient */}
      <DomainsClient kinds={[]} views={[]} />
    </>
  );
}

// ── /settings/workspace/sections ────────────────────────────────────────────

function WorkspaceSections() {
  // Registry catalog is the canonical nine sections. Live enablement state is
  // toggled via the workspace enablement POST; without a list GET we default
  // every registry row to enabled and let optimistic toggles write truth.
  const rows: SectionEnablementVm[] = SECTION_REGISTRY_UI.map((s) => ({
    slug: s.slug,
    name: s.name,
    enabled: true,
    legacy: Boolean((s as { legacy?: boolean }).legacy),
    description: s.briefSpec?.description ?? "",
    statusHint: null,
  }));

  async function onSetEnabled(slug: string, enabled: boolean): Promise<void> {
    await apiCall(sectionEnablement.setWorkspace, {
      body: { slug, enabled },
    });
  }

  return (
    <SettingsPageShell
      title="Sections"
      description="Workspace-tier defaults for the All-projects rail and the home rollup — immediate on/off toggles that save themselves on click."
    >
      <SectionEnablementList
        rows={rows}
        onSetEnabled={onSetEnabled}
        scope="workspace"
      />
    </SettingsPageShell>
  );
}

// ── /settings/workspace/team/* ──────────────────────────────────────────────

function TeamOverview() {
  return (
    <SettingsPageShell
      title="Multiplayer"
      description="Set up multiplayer and manage who has access to this workspace."
    >
      <OverviewPanel />
    </SettingsPageShell>
  );
}

function TeamDevices() {
  return (
    <SettingsPageShell
      title="Devices"
      description="Machines signed into this workspace. Revoke a lost or retired device."
    >
      <DevicesPanel />
    </SettingsPageShell>
  );
}

function TeamMembers() {
  return (
    <SettingsPageShell
      title="Members"
      description="People with access, their roles, and pending invites."
    >
      <MembersPanel />
    </SettingsPageShell>
  );
}

function TeamPrimary() {
  return (
    <SettingsPageShell
      title="Primary & sync"
      description="Provision the owned libSQL primary, connect this node, and watch live convergence."
    >
      <PrimaryPanel />
    </SettingsPageShell>
  );
}

function TeamRoles() {
  return (
    <SettingsPageShell
      title="Roles & permissions"
      description="Who can change what. Roles carry capability grants; assigning the first role switches enforcement on."
    >
      <RoleGrantsEditor />
    </SettingsPageShell>
  );
}

export const settingsWorkspaceArchiveRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/workspace/archive",
  component: WorkspaceArchive,
});

export const settingsWorkspaceDataRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/workspace/data",
  component: WorkspaceData,
});

export const settingsWorkspaceDirectoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/workspace/directory",
  component: WorkspaceDirectory,
});

export const settingsWorkspaceDomainsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/workspace/domains",
  component: WorkspaceDomains,
});

export const settingsWorkspaceSectionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/workspace/sections",
  component: WorkspaceSections,
});

export const settingsWorkspaceTeamRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/workspace/team",
  component: TeamOverview,
});

export const settingsWorkspaceTeamDevicesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/workspace/team/devices",
  component: TeamDevices,
});

export const settingsWorkspaceTeamMembersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/workspace/team/members",
  component: TeamMembers,
});

export const settingsWorkspaceTeamPrimaryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/workspace/team/primary",
  component: TeamPrimary,
});

export const settingsWorkspaceTeamRolesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/workspace/team/roles",
  component: TeamRoles,
});
